import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  Injector,
  input,
  output,
  Signal,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule} from '@angular/forms';

import {InputTextModule} from 'primeng/inputtext';
import {FloatLabelModule} from 'primeng/floatlabel';
import {IftaLabelModule} from 'primeng/iftalabel';
import {IconFieldModule} from 'primeng/iconfield';
import {InputIconModule} from 'primeng/inputicon';
import {InputMaskModule} from 'primeng/inputmask';
import {MessageModule} from 'primeng/message';
import {ValidationErrorsDictionary} from '../../../../core/dictionary/validation-errors.dictionary';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NoWhitespaceDirective} from '../../../../core/directives/no-whitespaces.directive';
import {UppercaseDirective} from '../../../../core/directives/uppercase.directive';

export type LabelMode = 'float' | 'ifta' | 'static' | 'none';
export type InputSize = 'small' | 'large';
export type IconPosition = 'left' | 'right';

export interface TextInputIcon {
  icon: string;
  position: IconPosition;
  clickable?: boolean;
}

@Component({
  selector: 'app-text-input',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    FloatLabelModule,
    IftaLabelModule,
    IconFieldModule,
    InputIconModule,
    InputMaskModule,
    MessageModule,
    NoWhitespaceDirective,
    UppercaseDirective,
  ],
  templateUrl: './text-input.component.html',
  styleUrl: './text-input.component.css',
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppTextInputComponent),
      multi: true,
    },
  ],
})
export class AppTextInputComponent implements ControlValueAccessor, AfterViewInit {

  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private ngControl: NgControl | null = null;

  readonly inputId = input<string>(`input-${crypto.randomUUID().slice(0, 8)}`);
  readonly label = input<string>('');
  readonly placeholder = input<string>('');
  readonly labelMode = input<LabelMode>('float');
  readonly size = input<InputSize | undefined>(undefined);
  readonly fluid = input<boolean>(true);
  readonly helpText = input<string>('');
  readonly filled = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly type = input<string>('text');
  readonly mask = input<string | null>(null);
  readonly slotChar = input<string>('_');
  readonly icon = input<TextInputIcon | null>(null);
  readonly showErrors = input<boolean>(true);
  readonly maxlength = input<number | null>(null);
  readonly name = input<string>('');
  readonly readonly = input<boolean>(false);
  readonly autofocus = input<boolean>(false);
  readonly noWhitespace = input<boolean>(false);
  readonly uppercase = input<boolean>(false);

  readonly focused = output<FocusEvent | Event>();
  readonly blurred = output<FocusEvent | Event>();
  readonly iconClicked = output<void>();
  readonly valueChanged = output<string>();

  readonly value = signal<string>('');
  readonly isFocused = signal<boolean>(false);
  readonly isTouched = signal<boolean>(false);
  readonly isDisabled = signal<boolean>(false);
  readonly controlState = signal<{
    invalid: boolean;
    touched: boolean;
    dirty: boolean;
    errors: any;
  }>({
    invalid: false,
    touched: false,
    dirty: false,
    errors: null,
  });

  readonly computedDisabled = computed(() =>
    this.disabled() || this.isDisabled()
  );

  readonly hasRequiredRule: Signal<boolean> = computed(() => {
    const state = this.controlState();
    return state.errors?.['required'] !== undefined;
  });

  readonly isInvalid = computed(() => {
    const state = this.controlState();
    return state.invalid && (state.touched || state.dirty);
  });

  readonly errorMessage = computed(() => {
    const state = this.controlState();
    if (!state.errors) return null;
    return ValidationErrorsDictionary.resolveValidationErrors(state.errors);
  });

  readonly useMask = computed(() => !!this.mask());
  readonly hasLabel = computed(() =>
    !!this.label() && this.labelMode() !== 'none'
  );
  readonly hasIcon = computed(() => !!this.icon());

  private onChange: (value: string) => void = () => {
  };
  private onTouched: () => void = () => {
  };

  private updateControlState(): void {
    if (this.ngControl?.control) {
      const ctrl = this.ngControl.control;
      this.controlState.set({
        invalid: ctrl.invalid,
        touched: ctrl.touched,
        dirty: ctrl.dirty,
        errors: ctrl.errors,
      });
    }
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  ngAfterViewInit(): void {
    this.ngControl = this.injector.get(NgControl, null, {
      optional: true,
      self: true,
    });

    if (this.ngControl?.control) {
      const ctrl = this.ngControl.control;

      ctrl.statusChanges?.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.updateControlState();
        });

      this.updateControlState();
    }
  }

  protected onFocus(event: FocusEvent): void {
    this.isFocused.set(true);
    this.focused.emit(event);
  }

  protected onMaskFocus(event: Event): void {
    this.isFocused.set(true);
    this.focused.emit(event);
  }

  protected onBlur(event: FocusEvent): void {
    this.isFocused.set(false);
    this.isTouched.set(true);
    this.onTouched();
    this.blurred.emit(event);
    this.updateControlState();
  }

  protected onMaskBlur(event: Event): void {
    this.isFocused.set(false);
    this.isTouched.set(true);
    this.onTouched();
    this.blurred.emit(event);
    this.updateControlState();
  }

  protected onIconClick(): void {
    if (this.icon()?.clickable) {
      this.iconClicked.emit();
    }
  }

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.value.set(value);
    this.onChange(value);
    this.valueChanged.emit(value);
  }

  protected onMaskInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value ?? '';

    this.value.set(value);
    this.onChange(value);
    this.valueChanged.emit(value);
  }
}
